import { useState, useEffect, useCallback } from 'react'
import api from '../lib/axios'

// ── useSharing ────────────────────────────────────────────────────────────────
/**
 * Manages share state for a single artifact.
 *
 * @param {string} type  'document' | 'specification' | 'test-plan'
 * @param {number} id
 */
export function useSharing(type, id) {
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [working, setWorking] = useState(false)
  const [error, setError]     = useState(null)

  const load = useCallback(async () => {
    if (!type || !id) return
    setLoading(true)
    try {
      const { data: d } = await api.get(`/artifacts/${type}/${id}/share`)
      setData(d)
    } catch {
      setError('Failed to load sharing settings.')
    } finally {
      setLoading(false)
    }
  }, [type, id])

  useEffect(() => { load() }, [load])

  const enable = async (expiresInDays = null) => {
    setWorking(true)
    try {
      const { data: d } = await api.post(`/artifacts/${type}/${id}/share/enable`, { expires_in_days: expiresInDays })
      setData(prev => ({ ...prev, ...d, is_share_active: true, is_public: true }))
      return { ok: true, shareUrl: d.share_url }
    } catch {
      return { ok: false }
    } finally {
      setWorking(false)
    }
  }

  const disable = async () => {
    setWorking(true)
    try {
      await api.post(`/artifacts/${type}/${id}/share/disable`)
      setData(prev => ({ ...prev, is_public: false, is_share_active: false }))
      return { ok: true }
    } catch {
      return { ok: false }
    } finally {
      setWorking(false)
    }
  }

  const revoke = async () => {
    setWorking(true)
    try {
      await api.post(`/artifacts/${type}/${id}/share/revoke`)
      setData(prev => ({ ...prev, share_token: null, share_url: null, is_public: false, is_share_active: false }))
      return { ok: true }
    } catch {
      return { ok: false }
    } finally {
      setWorking(false)
    }
  }

  const updateExpiry = async (expiresInDays) => {
    setWorking(true)
    try {
      const { data: d } = await api.put(`/artifacts/${type}/${id}/share`, { expires_in_days: expiresInDays })
      setData(prev => ({ ...prev, share_expires_at: d.share_expires_at }))
      return { ok: true }
    } catch {
      return { ok: false }
    } finally {
      setWorking(false)
    }
  }

  return { data, loading, working, error, enable, disable, revoke, updateExpiry, reload: load }
}

// ── useVersions ───────────────────────────────────────────────────────────────
export function useVersions(type, id) {
  const [versions, setVersions] = useState([])
  const [loading, setLoading]   = useState(true)

  const load = useCallback(async () => {
    if (!type || !id) return
    try {
      const { data } = await api.get(`/artifacts/${type}/${id}/versions`)
      // The endpoint wraps the list in { data: [...] } — assigning the envelope
      // straight to state made `versions` an object, so every .map() over it threw.
      setVersions(data.data ?? [])
    } finally {
      setLoading(false)
    }
  }, [type, id])

  useEffect(() => { load() }, [load])

  return { versions, loading, reload: load }
}

// ── useDiffStatus ─────────────────────────────────────────────────────────────
/**
 * Polls the diff status for an artifact — how many commits behind, what changed.
 *
 * @param {string} type
 * @param {number} id
 * @param {boolean} autoCheck  run immediately on mount
 */
export function useDiffStatus(type, id, autoCheck = true) {
  const [status, setStatus]   = useState(null)
  const [loading, setLoading] = useState(false)
  const [regen, setRegen]     = useState({ loading: false, result: null })

  const check = useCallback(async () => {
    if (!type || !id) return
    setLoading(true)
    try {
      const { data } = await api.get(`/artifacts/${type}/${id}/diff-status`)
      setStatus(data)
    } catch {
      setStatus(null)
    } finally {
      setLoading(false)
    }
  }, [type, id])

  useEffect(() => { if (autoCheck) check() }, [check, autoCheck])

  const regenerate = async (mode = 'auto') => {
    setRegen({ loading: true, result: null })
    try {
      const { data } = await api.post(`/artifacts/${type}/${id}/regenerate`, { mode })
      setRegen({ loading: false, result: data })
      // Refresh status
      await check()
      return data
    } catch (err) {
      const result = { queued: false, message: err.response?.data?.message ?? 'Regeneration failed.' }
      setRegen({ loading: false, result })
      return result
    }
  }

  return { status, loading, check, regenerate, regenLoading: regen.loading, regenResult: regen.result }
}
